import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DocumentAcceptance, LegalDocumentType, PendingDocument } from 'src/app/interfaces';
import { AuthService } from 'src/app/services/auth.service';
import { ConsentService } from 'src/app/services/consent.service';
import { HttpService } from 'src/app/services/http.service';
import { LegalDocumentsService } from 'src/app/services/legal-documents.service';
import { LegalConsentCheckboxComponent } from '../legal-consent-checkbox/legal-consent-checkbox.component';

/** One row on the screen: a document to read and a box to tick. */
interface PendingRow {
  document: PendingDocument;
  titleKey: string;
  changeSummary: string | null;
}

/**
 * The blocking acceptance gate. `AcceptLegalGateService` owns when it appears, and opens it over
 * whatever page the user is on: routing away would cost a scanned QR code its catalog.
 *
 * It cannot be dismissed — no close button, no backdrop, no Esc — so the only ways out are
 * accepting or signing out; a document deferrable indefinitely is not one that has been consented
 * to. Still only a UX affordance: the API refuses account creation, credential issuance and every
 * cooler open while acceptance is owed, whatever the client renders.
 *
 * Every box starts unchecked and is ticked one at a time. Pre-ticking, or one box covering both
 * documents, would fail the *inequívoco* and *específico* requirements of Art. 12 (and, for
 * pre-ticking, CJEU *Planet49*).
 */
@Component({
  selector: 'app-accept-legal-dialog',
  templateUrl: './accept-legal-dialog.component.html',
  standalone: true,
  imports: [
    CommonModule,
    LegalConsentCheckboxComponent,
    MatButtonModule,
    MatProgressSpinnerModule,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AcceptLegalDialogComponent implements OnInit {
  private readonly consent = inject(ConsentService);
  private readonly legalDocuments = inject(LegalDocumentsService);
  private readonly http = inject(HttpService);
  private readonly auth = inject(AuthService);
  private readonly translate = inject(TranslateService);
  private readonly dialogRef = inject(
    MatDialogRef<AcceptLegalDialogComponent, DocumentAcceptance[] | null>
  );

  readonly rows = signal<PendingRow[]>([]);
  readonly checked = signal<Record<string, boolean>>({});
  readonly isLoading = signal(true);

  readonly allChecked = computed(() => {
    const rows = this.rows();
    const checked = this.checked();
    return rows.length > 0 && rows.every(row => checked[row.document.documentType]);
  });

  async ngOnInit(): Promise<void> {
    await this.buildRows();
    if (this.rows().length === 0) {
      // Nothing is owed after all — most likely accepted in another tab. An empty card with a
      // permanently disabled button and no way out would strand the user, so close instead.
      this.dialogRef.close(null);
      return;
    }
    this.isLoading.set(false);
  }

  isChecked(type: LegalDocumentType): boolean {
    return !!this.checked()[type];
  }

  toggle(type: LegalDocumentType, value: boolean): void {
    // Replaced rather than mutated: OnPush does not re-render an object changed in place.
    this.checked.set({ ...this.checked(), [type]: value });
  }

  /**
   * Naming the version is what guarantees the text on screen is the text being consented to — a
   * cached body from earlier in the session would otherwise be shown against the new version
   * number.
   */
  openDocument(type: LegalDocumentType): void {
    const version = this.rows().find(row => row.document.documentType === type)?.document.version;
    if (type === LegalDocumentType.TNC) {
      this.legalDocuments.openTerms(version);
    } else {
      this.legalDocuments.openDataPrivacyPolicy(version);
    }
  }

  /**
   * Hand the acceptances to `AcceptLegalGateService` and get out of the way. Closing on the tick
   * rather than the response keeps a user standing at a cooler from waiting on a round trip; the
   * gate service sends them and brings this screen back if the server refuses.
   */
  accept(): void {
    if (!this.allChecked()) return;
    const lang = this.translate.currentLang || 'es';
    this.dialogRef.close(
      this.rows().map(row => ({
        documentType: row.document.documentType,
        version: row.document.version,
        lang,
      }))
    );
  }

  /**
   * Refusing has to remain possible without meaning "keep using the service with no lawful basis",
   * so signing out is the alternative. Closed first, because signing out navigates to the public
   * shell and a blocking dialog left over it would have no owner and no way out.
   */
  async logout(): Promise<void> {
    this.dialogRef.close(null);
    try {
      await this.auth.logout();
    } catch (error) {
      console.error('Could not sign out from the acceptance gate.', error);
    }
  }

  /**
   * Rebuild the rows from what the user owes. Failing to fetch the change summaries only costs the
   * summary line, so it must not block the screen the user is being held on.
   */
  private async buildRows(): Promise<void> {
    await this.consent.load(true);
    const pending = this.consent.pendingDocuments();
    let summaries: Partial<Record<LegalDocumentType, string | null>> = {};
    try {
      const response = await this.http.getLegalDocumentRequirements(this.translate.currentLang || 'es');
      if (response?.successful && response.data) {
        for (const requirement of response.data) {
          summaries[requirement.documentType] = requirement.changeSummary;
        }
      }
    } catch (error) {
      console.error('Could not load the localized change summaries.', error);
    }
    this.rows.set(
      pending.map(document => ({
        document,
        titleKey:
          document.documentType === LegalDocumentType.TNC
            ? 'legal_documents.tnc_title'
            : 'legal_documents.dpp_title',
        changeSummary: summaries[document.documentType] ?? document.changeSummary,
      }))
    );
    this.checked.set({});
  }
}
